// 物販 (お土産) レジ・在庫オペレーション API (#1182, epic #1181)。
// 店舗オーナー・スタッフの店頭 POS / 在庫調整 + 自社EC 連携 (#1184)。
// 配置ルールは index.ts 参照。飲食の卓会計 (orders.ts / storeOwner.ts) と
// 将来独立に育てるため別ドメインに分離。
import { API_BASE, ApiError, apiFetch, trackedFetch } from './_client'
import type { RegisterPayMethod } from '../../lib/storePayment'

// #1184 EC商品ピッカー用の最小型 (public GET /ec/products の抜粋)
export type EcProductSummary = {
  id: string
  name_ja: string
  name_en?: string
  price_jpy: number
  vendor_id?: string | null
  // #1723 'auto' = 掛け率で自動値付け (店頭の通常価格に追従) / 'manual' = EC の管理者が固定
  price_source?: 'auto' | 'manual' | null
}

// #1897 店舗から見た EC 商品の状態。公開の /ec/products は下書きを返さないので、
// 店舗用の API (GET /stores/{id}/ec-products) で取る
// #2271 EC に公開するには**価格とサイズ・重量の両方**が要る。
// 「値付け待ち」に畳まず、**何を待っているのかを名指しする** —
// 入れるのは運営なので、店には「待ち」の内容だけ伝わればよい
export type StoreEcStatus =
  | 'draft' | 'pending' | 'pending_size' | 'pending_price_size'
  | 'published' | 'sold_out' | 'hidden'
/**
 * #2266 **商品そのもの**の大きさと重さ (裸で測った値)。
 *
 * **送る箱の大きさではない。** 箱・緩衝材・封筒のぶんは backend の
 * `services/delivery/yamato_fare.py` が後から足す (`BOX_MARGIN_CM` = 各辺 3cm /
 * `BOX_WEIGHT_KG` = 1 箱 0.3kg / `SMALL_PARCEL_MARGIN_CM`)。ここに包んだ後の寸法を
 * 入れると**二重に見込んで客が払いすぎる**。
 *
 * 箱で聞かない理由はもう 1 つある: **2 個以上まとめて買われたときに計算できない。**
 * 何個が 1 箱に収まるかは個数で変わるので、商品に持たせるのは裸の寸法でなければ
 * ならない (オーナー判断 2026-10-23)。
 */
export type EcBoxSize = {
  lengthCm: number
  widthCm: number
  heightCm: number
  weightKg: number
}
export type StoreEcProductStatus = {
  itemId: string
  ecProductId: string
  status: StoreEcStatus
  priceJpy: number
  priceSource?: 'auto' | 'manual' | null
  nameJa: string
  /**
   * #2266 公開に箱の大きさが足りないか。**状態 (`status`) では分からない** —
   * 下書きのうちは足りないことが見えず、公開を押して初めて「サイズ・重量待ち」になる。
   * これを見て、公開の手前で店に聞く。
   */
  needsBox?: boolean
  box?: Partial<EcBoxSize>
  /**
   * #2354 **この 1 点だけ買われたとき**の荷物のサイズと送料の目安。
   *
   * 寸法か重さが欠けていれば null (既定値で埋めた数字を目安として出すと、
   * 入れていない店に入れたつもりにさせる)。
   *
   * **宛先で金額が変わる**ので 1 つの数字にしていない。県内と最遠の 2 つを返し、
   * 画面は幅で出す。「どちらでもよい」の店は、その宛先で安い方の会社が入る。
   */
  fare?: EcFareEstimate | null
}
export type StoreEcItemResult = {
  itemId: string
  ecProductId?: string
  result: 'published' | 'pending' | 'draft' | 'unchanged' | 'not_linked'
  status?: StoreEcStatus
}

// #1897 EC 出店の申請 (店舗が申請 → 運営が承認すると出店者ができて紐づく)
export type StoreEcApplicationStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn'
// #1897 EC の店ページに出す住所の範囲 (city=市区町村まで [既定] / full=番地まで / hidden=出さない)
export type EcAddressVisibility = 'city' | 'full' | 'hidden'

// #2306 配送業者の 3 択。**客に見せる送料がこれで決まる**
//   yamato    … 宅急便の額
//   japanpost … ゆうパックの額
//   either    … 安い方 (客が最安。荷物ごとにどちらで送るかが変わる)
export type EcCarrier = 'yamato' | 'japanpost' | 'either'

/** #2306 その店の所在地から送るときの、1 地帯 1 サイズぶんの比較 */
export type EcCarrierFare = {
  size: number
  // どちらも地帯の中で割れることがある (ヤマトは同一都道府県内運賃、
  // ゆうパックは都道府県ごとの運賃)。min と max が違うときは画面で幅として出す
  yamatoMinJpy: number
  yamatoMaxJpy: number
  japanpostMinJpy: number
  japanpostMaxJpy: number
  // 'either' = この地帯の中で、県によってどちらが安いか変わる (印を付けない)
  cheaper: EcCarrier
}

export type EcCarrierSettings = {
  carrier: EcCarrier
  originPrefecture: string | null
  sizes: number[]
  zones: { zone: string; label: string; fares: EcCarrierFare[] }[]
  // 小型便は業者で変わらない (ゆうパケット)。表には出さず注記に使う
  smallParcelJpy: number
}
// #2272 EC の売上 1 件 (= 1 注文のうち、その店のぶん)。
// **足し算が画面で合うように**、基準額 (仕入値 or 商品代) まで返す:
//   お支払額 = 基準額 - 販促協力金 + 送料 - 取消手数料
export type StoreEcSale = {
  orderId: string
  /** 注文の中でこの行を一意に指すキー（1 注文に自店の行が 2 つ出ることがある） */
  settlementKey: string
  orderedAt: string | null
  /** 'confirmed' | 'shipped' | 'refunded' */
  orderStatus: string
  itemLines: string[]
  /** 商品代 = 定価 x 数量。**お客様が払った額ではない** (プロモ割引は当社負担) */
  itemsSubtotalJpy: number
  /** 仕入値 x 数量。null = 仕入値が未設定の商品を含む */
  costSubtotalJpy: number | null
  /** 'cost' | 'price'。お支払額の基準にどちらを使ったか */
  payoutBasis: string
  /** この店が受け取る額 */
  payoutJpy: number
  shippingJpy: number
  incentiveJpy: number
  cancellationFeeJpy: number
  /** 'awaiting_shipment' | 'awaiting_approval' | 'approving' | 'manual_payout' | 'paid' | 'rejected' | 'blocked' */
  settlementStatus: string
  paidAt: string | null
}

// #2272 合計は**サーバが数える**。画面でも数えると数え方が 2 つになる
export type StoreEcSales = {
  /** 既に振り込まれた額 */
  paidJpy: number
  /** これから入る見込みの額 */
  pendingJpy: number
  /** 止まっている額 (差し戻し・保留)。0 なら画面に出さない */
  onHoldJpy: number
  /** 何件の注文を見たか / 何件まで返すか。**合計はこの範囲のぶん** */
  scannedOrders: number
  rowLimit: number
  rows: StoreEcSale[]
}
// #2268 送料割引: お店が送料の一部または全額をもつ。**負担するのはお店で弊社ではない** —
// お客様が払わなかったぶん、お店が受け取る額も減る
export type EcShippingSubsidyMode = 'none' | 'free' | 'cap' | 'threshold'
export type EcShippingSubsidy = EcCarrierSettings & {
  mode: EcShippingSubsidyMode
  /** mode='cap': 1 便あたりお店が持つ上限 */
  capJpy: number
  /** mode='threshold': その便の商品代がこの額以上なら送料無料 */
  thresholdJpy: number
  maxCapJpy: number
  maxThresholdJpy: number
}

// #1897 まとめて EC に出す: 1 行ごとに反映した後にしたい状態
export type StoreEcBulkTarget = 'draft' | 'published'
// 反映の予定 1 行。removed = EC から外す (チェックを外した連携済みの商品)
export type StoreEcPlanItem = {
  itemId: string
  target: StoreEcBulkTarget | 'removed'
  /** #2266 公開のときに店が入れた箱の大きさ。**後から書いた方を採る** */
  box?: EcBoxSize
}
/** #1897 店舗から見た EC の状態 (通販タブの一覧と、店ページの住所の設定) */
export type StoreEcProducts = {
  linked: boolean
  vendorId?: string
  vendorSlug?: string | null
  items: StoreEcProductStatus[]
  /**
   * #2344 オーナーが現行版の EC 出店規約に同意しているか。
   *
   * **画面のロックはこれで決める。** オーナーの同意はオーナーのログインでしか
   * 読めないので、店長・スタッフの画面では「未同意」に倒れていた
   * (サーバは店長の出品を通すのに、画面だけ閉じていた)。
   */
  termsAgreed?: boolean
  /**
   * #1957 **店が EC から外した商品** (itemId -> 外した時刻 ISO)。
   *
   * 連携が無いので `items` には出ない。画面はこれを見て「一度も出していない」と
   * 「意図して下ろした」を分ける。分けないと「すべて選ぶ」が意図して下ろした商品まで
   * 拾い、黙って EC に戻る。
   */
  unlistedAt?: Record<string, string>
  // #1897 EC の店ページに出す住所の範囲と地域
  addressVisibility?: EcAddressVisibility
  addressArea?: string | null
  address?: string | null
}

/** #2354 1 点ぶんの送料の目安 */
export type EcFareEstimate = {
  /** 「60サイズ」「小型便」など。会社を選んでいる店は「ヤマト60サイズ」 */
  label: string
  nearJpy: number
  nearCarrier: 'yamato' | 'japanpost' | 'either'
  farJpy: number
  farCarrier: 'yamato' | 'japanpost' | 'either'
  farPrefecture: string | null
  originPrefecture: string | null
}

export type StoreEcBulkResult = {
  itemId: string
  target: StoreEcBulkTarget | 'removed'
  status: StoreEcStatus | null
  /** 反映できなかった理由 (サーバの code)。反映できたら null */
  error: string | null
}
export type StoreEcApplication = {
  storeId: string
  status: StoreEcApplicationStatus
  storeName: string
  descriptionJa: string
  address: string
  prefecture: string
  city: string
  phone: string
  hours: string
  retailItemCount: number
  addressVisibility: EcAddressVisibility
  termsVersion: string
  submittedAt: string | null
  decidedAt: string | null
  withdrawnAt: string | null
  rejectReason: string | null
  vendorId: string | null
  vendorSlug: string | null
}
export type StoreEcApplicationState = {
  /** 既に EC の出店者と紐づいているか (承認済み) */
  linked: boolean
  vendorSlug: string | null
  application: StoreEcApplication | null
  eligibility: {
    kycApproved: boolean
    retailItemCount: number
    termsVersion: string
    agreedTermsVersion: string | null
  }
  /** 申請に写す PAY の店舗情報 */
  prefill: { storeName: string; address: string; prefecture: string; city: string; phone: string; hours: string; descriptionJa: string }
}

export type RetailSaleItem = {
  itemId: string
  name: string
  price: number
  quantity: number
}

// #1365 在庫が動いた理由。台帳 1 本に集約している (バラバラの機能に見えて、
// 実体は全て「理由付きの在庫の増減」なので分けない)。
export type StockMovementReason = 'receive' | 'sale' | 'return' | 'stocktake' | 'adjust'

export type StockMovement = {
  id: string
  itemId: string
  itemName: string
  delta: number
  before: number | null
  after: number | null
  reason: StockMovementReason
  // actor は uid / memberId (画面には出さない)。表示は actorName、無ければ役割ラベル。
  actor: string | null
  actorName: string
  actorRole: string | null
  note: string
  createdAt: string | null
}

export type StockReceiveResultLine = {
  itemId: string
  name: string
  before: number
  after: number
  quantity: number
}

// #1379 CSV 一括登録のプレビュー / 適用結果。
export type ImportRowAction = 'create' | 'update' | 'skip' | 'error'

export type ImportRow = {
  line: number                      // CSV の行番号 (店主が Excel で探せるように)
  name: string
  action: ImportRowAction
  error: string | null
  itemId: string | null
  matchedBy: 'barcode' | 'name' | null
  values: Record<string, string | number>
  before: Record<string, string | number | null> | null
}

export type ImportPreview = {
  counts: { create: number; update: number; skip: number; error: number }
  rows: ImportRow[]
  // ヘッダ不正・行数超過など「1行も取り込めない」理由。HTTP エラーではなく
  // 結果として返る (apiFetch は backend detail を UI に出さない規約のため、
  // 400 にすると「読み取れませんでした」に潰れて理由が伝わらない)。
  fatalErrors: string[]
}

// #1379 PR B 仕入先マスタ。
export type Supplier = {
  id: string
  name: string
  tel: string
  // 発注先メール。設定するとこの仕入先の商品のアラートだけがここへ届く。
  // 空なら従来どおり店舗の送信先へ集約される。
  email: string
  note: string
  leadTimeDays: number
}

export type SupplierInput = Omit<Supplier, 'id'>

// 発注点の提案。suggested が null = 実績不足で提案しない。
export type ReorderSuggestionReason =
  | 'unset' | 'increase' | 'decrease' | 'already_set' | 'not_enough_sales' | 'ok'

export type ReorderSuggestion = {
  itemId: string
  name: string
  stock: number
  currentReorderPoint: number | null
  soldInPeriod: number
  dailySales: number
  leadTimeDays: number
  suggested: number | null
  reason: ReorderSuggestionReason
}

export type StocktakeResultLine = {
  itemId: string
  name: string
  before: number    // 帳簿在庫
  after: number     // 実棚で数えた数
  delta: number     // 差異 (マイナス = ロス)。0 の行も含まれる
}

export type RetailSaleResult = {
  transactionId?: string
  amount: number
  items: RetailSaleItem[]
  shortages: { itemId: string; requested: number; available: number }[]
}

export const retailApi = {
  // 在庫クイック調整 (入荷・棚卸・返品戻し)。ALL_ROLES。
  // stock>=1 で soldOut 解除、0 で soldOut。clear=true で在庫管理をやめる。
  async setRetailMenuStock(storeId: string, itemId: string, stock: number | null) {
    const res = await apiFetch(
      `/retail/${storeId}/menu/${encodeURIComponent(itemId)}/stock`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stock === null ? { clear: true } : { stock }),
      },
    )
    return res.json()
  },

  // 物販の現金会計 (CAN_APPROVE_CASH)。金額はサーバ再計算。idempotencyKey で
  // 二重タップ防止 (同キー再送は同じ tx を返す)。
  async retailCashSale(
    storeId: string,
    items: { itemId: string; quantity: number }[],
    idempotencyKey: string,
    method: RegisterPayMethod = 'cash',  // #2162 現金 / 店の端末
  ) {
    const res = await apiFetch(`/retail/${storeId}/cash-sale`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, idempotencyKey, method }),
    })
    return res.json() as Promise<RetailSaleResult>
  },

  // 客QR決済縮退用: 在庫減算のみ (金銭記録なし)。ALL_ROLES + 監査。
  async retailDecrement(storeId: string, items: { itemId: string; quantity: number }[]) {
    const res = await apiFetch(`/retail/${storeId}/decrement`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
    return res.json() as Promise<RetailSaleResult>
  },

  // #1181 follow-up 在庫アラート送信先のテスト送信 (OWNER_OR_MANAGER)。
  // 在庫アラートは在庫が減ったときにしか飛ばないため、宛先の打ち間違いに気付けない。
  // sent=false は送信基盤未設定 (local の RESEND_API_KEY 無し) か送信失敗。
  async sendReorderAlertTest(storeId: string) {
    const res = await apiFetch(`/retail/${storeId}/reorder-alert/test`, { method: 'POST' })
    return res.json() as Promise<{ sent: boolean; recipients: string[] }>
  },

  // #1365 入荷・返品戻し: 複数商品の在庫を数量指定でまとめて加算 (ALL_ROLES)。
  // 在庫クイック調整 (setRetailMenuStock) が絶対値の置き換えなのに対し、こちらは
  // 相対値の加算 — 段ボール1箱の検品で足し算させないため。
  // 入荷と返品は処理が同じなので同じ API を共有し、台帳の reason だけ変える。
  async receiveStock(
    storeId: string,
    items: { itemId: string; quantity: number }[],
    note = '',
    reason: 'receive' | 'return' = 'receive',
  ) {
    const res = await apiFetch(`/retail/${storeId}/receive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, note, reason }),
    })
    return res.json() as Promise<{ items: StockReceiveResultLine[]; unknown: string[] }>
  },

  // #1365 棚卸: 実棚で数えた数を絶対値で書き込み、帳簿との差異を返す (ALL_ROLES)。
  // 差異なしの行も戻り値に含まれる (数えたことが画面で分かるように)。
  async stocktake(
    storeId: string,
    items: { itemId: string; counted: number }[],
    note = '',
  ) {
    const res = await apiFetch(`/retail/${storeId}/stocktake`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, note }),
    })
    return res.json() as Promise<{ items: StocktakeResultLine[]; unknown: string[] }>
  },

  // #1365 在庫変動履歴 (新しい順)。itemId でその商品だけに絞れる。
  async listStockMovements(storeId: string, itemId?: string, limit = 100) {
    const params = new URLSearchParams({ limit: String(limit) })
    if (itemId) params.set('itemId', itemId)
    const res = await apiFetch(`/retail/${storeId}/movements?${params.toString()}`)
    return (await res.json()).movements as StockMovement[]
  },

  // #1379 CSV 一括登録。preview は**書き込まない** — 数百行が無確認で入ると、
  // 間違いに気付いたときには手で直せないため、必ず差分を見せてから apply する。
  async previewProductImport(storeId: string, csv: string) {
    const res = await apiFetch(`/retail/${storeId}/import/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ csv }),
    })
    return res.json() as Promise<ImportPreview>
  },

  async applyProductImport(storeId: string, csv: string) {
    const res = await apiFetch(`/retail/${storeId}/import/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ csv }),
    })
    return res.json() as Promise<ImportPreview & {
      applied: { created: number; updated: number; failed: number }
    }>
  },

  // 雛形 CSV。BOM 付き UTF-8 なので Excel でそのまま開ける。
  async downloadProductImportTemplate(storeId: string) {
    const res = await apiFetch(`/retail/${storeId}/import/template`)
    return res.text()
  },

  // #1379 PR B 仕入先マスタ。一覧は現場も見る (ALL_ROLES)、変更は OWNER_OR_MANAGER
  // — 発注先メールは通知の宛先になるので、送信先設定 (#1363) と同格に扱う。
  async listSuppliers(storeId: string) {
    const res = await apiFetch(`/retail/${storeId}/suppliers`)
    return (await res.json()).suppliers as Supplier[]
  },

  async createSupplier(storeId: string, body: SupplierInput) {
    const res = await apiFetch(`/retail/${storeId}/suppliers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return res.json() as Promise<Supplier>
  },

  async updateSupplier(storeId: string, supplierId: string, body: SupplierInput) {
    const res = await apiFetch(`/retail/${storeId}/suppliers/${supplierId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return res.json() as Promise<Supplier>
  },

  // 削除すると、参照している商品の supplierId も外れる (宛先解決が黙って
  // 店舗宛にフォールバックする状態を作らないため)。
  async deleteSupplier(storeId: string, supplierId: string) {
    const res = await apiFetch(`/retail/${storeId}/suppliers/${supplierId}`, { method: 'DELETE' })
    return res.json() as Promise<{ deleted: boolean; unlinkedItems: number }>
  },

  // #1363 で商品ごとの文字列として入れた仕入先名からマスタを起こして紐付ける。
  // 二度実行してもマスタは増えない。
  async migrateSuppliers(storeId: string) {
    const res = await apiFetch(`/retail/${storeId}/suppliers/migrate`, { method: 'POST' })
    return res.json() as Promise<{ created: number; linkedItems: number }>
  },

  // #1379 PR B 発注点の自動提案。**書き込まない** — 適用は applyReorderSuggestions。
  async getReorderSuggestions(storeId: string, days = 30) {
    const res = await apiFetch(`/retail/${storeId}/reorder-suggestions?days=${days}`)
    return res.json() as Promise<{ periodDays: number; suggestions: ReorderSuggestion[] }>
  },

  async applyReorderSuggestions(
    storeId: string,
    items: { itemId: string; reorderPoint: number }[],
  ) {
    const res = await apiFetch(`/retail/${storeId}/reorder-suggestions/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
    return res.json() as Promise<{ applied: number }>
  },

  // #1184 EC商品ピッカー: 自社EC の公開カタログを vendor で絞って返す (認証不要)。
  async listEcVendorProducts(vendorId: string) {
    const res = await trackedFetch(`${API_BASE}/ec/products`)
    if (!res.ok) throw new Error(`ec products ${res.status}`)
    const all = (await res.json()) as EcProductSummary[]
    return all.filter((p) => p.vendor_id === vendorId)
  },

  // #1723 出店者の価格ルール (auto / manual)。公開エンドポイントは掛け率を返さない (mode だけ)。
  // 商品タブで「掛け率で自動・追従します」の案内を出してよいかの判定に使う。
  async getEcVendorPricingMode(vendorId: string): Promise<'auto' | 'manual'> {
    const res = await trackedFetch(`${API_BASE}/ec/vendors/${encodeURIComponent(vendorId)}`)
    if (!res.ok) throw new Error(`ec vendor ${res.status}`)
    const v = (await res.json()) as { pricing_mode?: 'auto' | 'manual' }
    return v.pricing_mode === 'manual' ? 'manual' : 'auto'
  },

  // #1716 この物販商品を EC に出す: EC 商品を自動作成して連携する。以前に作った EC 商品が
  // あれば再利用する。#1723 出店者の価格ルールが auto (既定) なら通常価格 × 掛け率で
  // 値付けして即公開 (published=true)。manual の店は価格未設定・非表示で admin 待ち。
  async createEcProductFromMenuItem(storeId: string, itemId: string) {
    const res = await apiFetch(`/stores/${storeId}/menu/${itemId}/ec-product`, { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to list menu item on EC')
    return res.json() as Promise<{
      ecProductId: string
      created: boolean
      priceSet?: boolean
      published?: boolean
      pricingMode?: 'auto' | 'manual'
      alreadyLinked?: boolean
      // #1897 「EC に出す」は下書きを作る (公開は publishStoreEcProducts)
      draft?: boolean
      status?: StoreEcStatus
      product?: Pick<EcProductSummary, 'id' | 'name_ja' | 'price_jpy' | 'price_source'>
    }>
  },

  // #1897 店舗から見た EC 商品の状態 (下書きを含む)
  async listStoreEcProducts(storeId: string) {
    const res = await apiFetch(`/stores/${storeId}/ec-products`, { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to load EC product status')
    return res.json() as Promise<StoreEcProducts>
  },

  // #1897 下書きを公開する。価格が無いものは運営の値付け待ちへ回る
  async publishStoreEcProducts(storeId: string, itemIds: string[]) {
    const res = await apiFetch(`/stores/${storeId}/ec-products/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itemIds }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to publish EC products')
    return res.json() as Promise<{ items: StoreEcItemResult[] }>
  },

  // #1897 公開中の商品を下書きに戻す
  async unpublishStoreEcProducts(storeId: string, itemIds: string[]) {
    const res = await apiFetch(`/stores/${storeId}/ec-products/unpublish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itemIds }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to unpublish EC products')
    return res.json() as Promise<{ items: StoreEcItemResult[] }>
  },

  // #1897 EC でのプレビュー (ログイン不要の期限付きリンク) を発行する。
  // items を渡すと反映予定のプレビュー (まとめて EC に出す の確認画面から。何も書き換えない)
  async issueStoreEcPreview(storeId: string, items?: StoreEcPlanItem[]) {
    const res = await apiFetch(`/stores/${storeId}/ec-preview`, {
      method: 'POST',
      ...(items ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) } : {}),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to issue EC preview link')
    return res.json() as Promise<{ url: string; expiresAt: string }>
  },

  // #1897 EC 出店の申請の状態と、申請できるか (owner)
  async getStoreEcApplication(storeId: string) {
    const res = await apiFetch(`/stores/${storeId}/ec-application`, { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to load EC application')
    return res.json() as Promise<StoreEcApplicationState>
  },

  // #1897 EC への出店を申請する (owner)。規約への同意は先に agreeEcVendorTerms で記録する
  async applyStoreEcApplication(
    storeId: string,
    body: { descriptionJa: string; termsVersion: string; addressVisibility: EcAddressVisibility },
  ) {
    const res = await apiFetch(`/stores/${storeId}/ec-application`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to apply for EC')
    return res.json() as Promise<{ application: StoreEcApplication }>
  },

  // #1897 申請中の出店申請を取り下げる (owner)
  async withdrawStoreEcApplication(storeId: string) {
    const res = await apiFetch(`/stores/${storeId}/ec-application/withdraw`, { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to withdraw EC application')
    return res.json() as Promise<{ application: StoreEcApplication }>
  },

  // #1897 EC の店ページに出す住所の範囲を変える (owner)
  async setStoreEcAddressVisibility(storeId: string, addressVisibility: EcAddressVisibility) {
    const res = await apiFetch(`/stores/${storeId}/ec-vendor/address-visibility`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ addressVisibility }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update EC address visibility')
    return res.json() as Promise<{ addressVisibility: EcAddressVisibility; addressArea: string | null; address: string | null }>
  },

  // #2306 配送業者の設定と、その店からの比較表 (owner)
  async getStoreEcCarrier(storeId: string) {
    const res = await apiFetch(`/stores/${storeId}/ec-vendor/carrier`, { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to load EC carrier')
    return res.json() as Promise<EcCarrierSettings>
  },

  // #2306 配送業者を変える (owner)。**客に見せる送料がこれで決まる**
  async setStoreEcCarrier(storeId: string, carrier: EcCarrier) {
    const res = await apiFetch(`/stores/${storeId}/ec-vendor/carrier`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ carrier }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update EC carrier')
    return res.json() as Promise<EcCarrierSettings>
  },

  // #2272 この店の EC の売上 (owner|manager)。**読み取りだけ** —
  // EC の返金は運営がやるので、店に返金の操作を出さない
  async getStoreEcSales(storeId: string, limit = 50) {
    const res = await apiFetch(`/stores/${storeId}/ec-sales?limit=${limit}`, { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to load EC sales')
    return res.json() as Promise<StoreEcSales>
  },

  // #2268 送料割引の設定と、額を読むための運賃の目安 (owner)
  async getStoreEcShippingSubsidy(storeId: string) {
    const res = await apiFetch(`/stores/${storeId}/ec-vendor/shipping-subsidy`, { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to load EC shipping subsidy')
    return res.json() as Promise<EcShippingSubsidy>
  },

  // #2268 送料割引を変える (owner)。**客に見せる送料とお店へのお支払いが両方変わる**
  async setStoreEcShippingSubsidy(
    storeId: string,
    setting: { mode: EcShippingSubsidyMode; capJpy: number; thresholdJpy: number },
  ) {
    const res = await apiFetch(`/stores/${storeId}/ec-vendor/shipping-subsidy`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(setting),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update EC shipping subsidy')
    return res.json() as Promise<EcShippingSubsidy>
  },

  // #2266 EC に出している商品の箱の大きさを直す。**入れさせておいて直せないのは
  // 片手落ち** — 緩衝材を変えた・箱を変えた、は普通に起きる
  async setStoreEcBox(storeId: string, itemId: string, box: EcBoxSize) {
    const res = await apiFetch(`/stores/${storeId}/ec-products/box`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itemId, box }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to save the box size')
    return res.json() as Promise<{ itemId: string; status: StoreEcStatus; box: EcBoxSize }>
  },

  // #1897 物販の商品をまとめて EC に出す (つながっていない商品は下書きを作り、選んだ状態にする)
  async bulkStoreEcProducts(storeId: string, items: StoreEcPlanItem[]) {
    const res = await apiFetch(`/stores/${storeId}/ec-products/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to apply EC products in bulk')
    return res.json() as Promise<{
      items: StoreEcBulkResult[]
      summary: { draft: number; published: number; pending: number; removed: number; failed: number }
    }>
  },
}
